import { useEffect, useState } from 'react'
import { DoseReference, doseTierLabel } from '../../../../../types/doseSuggestion.type'
import { useReferenceDose } from './useReferenceDose'

/** What "Use this dose" writes into the order form (every value overwrites the field). */
export type ReferenceDoseApply = {
  dose_quantity: string | null
  dose_unit: string | null
  route: string | null
  frequency_code: string | null
  medication_frequency_id: number | null
  once: boolean
}

type Props = {
  drug?: string
  form?: string
  strength?: string
  patientId?: string
  encounterId?: string
  onApply: (values: ReferenceDoseApply) => void
}

const MIN_KG = 0.3
const MAX_KG = 350

const physicalDose = (reference: DoseReference, strength: string | undefined) => {
  const quantity = reference.dose_quantity ? `${reference.dose_quantity} ${reference.dose_unit ?? ''}`.trim() : ''
  const withStrength = quantity && strength ? `${quantity} of ${strength}` : quantity
  if (withStrength) return withStrength
  return reference.dose_text ?? ''
}

const frequencyText = (reference: DoseReference) => {
  if (reference.once) return 'Once'
  if (reference.frequency_code) return reference.frequency_code
  return ''
}

const citationText = (reference: DoseReference) => {
  const citation = reference.detail?.citation
  if (!citation?.doc) return ''
  return citation.page != null && citation.page !== '' ? `${citation.doc}, p. ${citation.page}` : citation.doc
}

const WeightPrompt = ({
  onSave,
  saving,
  failed
}: {
  onSave: (kg: number) => Promise<void>
  saving: boolean
  failed: boolean
}) => {
  const [kg, setKg] = useState('')
  const [invalid, setInvalid] = useState(false)
  const value = Number(kg)
  const valid = kg.trim() !== '' && Number.isFinite(value) && value >= MIN_KG && value <= MAX_KG

  const save = async () => {
    if (!valid) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    await onSave(value)
  }

  return (
    <div className="mt-2 flex flex-col gap-2">
      <p className="m-0 text-sm text-[#3D3D3D]">A weight-based dose needs the patient&apos;s weight.</p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm">
          <span>Weight</span>
          <input
            aria-label="Weight (kg)"
            className="h-9 w-24 rounded-full border border-[#999999] px-3 text-sm outline-none focus:border-primary"
            inputMode="decimal"
            max={MAX_KG}
            min={MIN_KG}
            onChange={e => setKg(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void save()
              }
            }}
            step="0.1"
            type="number"
            value={kg}
          />
          <span>kg</span>
        </label>
        <button
          className="h-9 rounded-full border border-[#1565C0] px-4 text-sm font-medium text-[#1565C0] hover:bg-[#1565C0]/5 disabled:opacity-50"
          disabled={saving}
          onClick={() => void save()}
          type="button"
        >
          {saving ? 'Saving…' : 'Save weight'}
        </button>
      </div>
      {invalid ? (
        <p className="m-0 text-xs text-rose-600">
          Enter a weight between {MIN_KG} and {MAX_KG} kg.
        </p>
      ) : null}
      {failed ? <p className="m-0 text-xs text-rose-600">Could not save the weight. Try again.</p> : null}
    </div>
  )
}

/**
 * MedKB reference dose for the chosen drug (hint + "Use this dose"; never prefilled). Sits under
 * the dose row of the order form. Shows the weight prompt for weight-based rules without a recent
 * weight, a warning for high-alert medicines, and a plain note when nothing is on file.
 */
export const ReferenceDosePanel = ({ drug, form, strength, patientId, encounterId, onApply }: Props) => {
  const { enabled, suggestion, isLoading, isError, saveWeight, isSavingWeight, saveWeightError } = useReferenceDose({
    drug,
    encounterId,
    form,
    patientId,
    strength
  })
  const [weightSaved, setWeightSaved] = useState(false)

  useEffect(() => {
    setWeightSaved(false)
  }, [drug, form, strength])

  if (!enabled) return null

  const base = 'rounded-xl border border-[#E0E0E0] bg-[#F7F7F7] px-4 py-3'

  if (isLoading) {
    return (
      <div aria-live="polite" className={base} role="status">
        <p className="m-0 text-sm text-[#666666]">Looking up the reference dose…</p>
      </div>
    )
  }

  const reference = suggestion?.reference ?? null
  const highAlert = !!suggestion?.high_alert
  const weightNeeded = !!suggestion?.weight_needed

  const onSaveWeight = async (kg: number) => {
    await saveWeight(kg)
    setWeightSaved(true)
  }

  return (
    <div aria-label="Reference dose" className={base} role="region">
      {highAlert ? (
        <p className="m-0 text-sm font-medium text-[#B42318]">High-alert medicine — enter the dose yourself.</p>
      ) : null}

      {!highAlert && reference ? (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Reference dose</span>
            <span className="inline-flex items-center rounded-full border border-[#D0D5DD] bg-white px-2 py-px text-[11px] leading-4 text-[#344054]">
              {doseTierLabel(reference)}
            </span>
          </div>
          {reference.basis ? <p className="m-0 text-xs text-[#666666]">{reference.basis}</p> : null}
          <p className="m-0 text-sm">
            {[physicalDose(reference, strength), frequencyText(reference)].filter(Boolean).join(' · ')}
            {reference.duration_days
              ? ` · ${reference.duration_days} day${reference.duration_days === 1 ? '' : 's'}`
              : ''}
          </p>
          {reference.max_single_text || reference.max_daily_text ? (
            <p className="m-0 text-xs text-[#666666]">
              {[
                reference.max_single_text ? `Max single ${reference.max_single_text}` : '',
                reference.max_daily_text ? `Max daily ${reference.max_daily_text}` : ''
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}
          {reference.detail?.indication || reference.detail?.population ? (
            <p className="m-0 text-xs text-[#666666]">
              {[reference.detail?.indication, reference.detail?.population].filter(Boolean).join(' · ')}
            </p>
          ) : null}
          {citationText(reference) ? <p className="m-0 text-xs text-[#666666]">{citationText(reference)}</p> : null}
          <div>
            <button
              className="mt-1 h-9 rounded-full bg-[#1565C0] px-4 text-sm font-medium text-white hover:bg-[#0D47A1]"
              onClick={() =>
                onApply({
                  dose_quantity: reference.dose_quantity,
                  dose_unit: reference.dose_unit,
                  frequency_code: reference.frequency_code,
                  medication_frequency_id: reference.medication_frequency_id,
                  once: !!reference.once,
                  route: reference.route
                })
              }
              type="button"
            >
              Use this dose
            </button>
          </div>
        </div>
      ) : null}

      {weightNeeded && !highAlert ? (
        <WeightPrompt failed={saveWeightError} onSave={onSaveWeight} saving={isSavingWeight} />
      ) : null}

      {!highAlert && !reference && !weightNeeded ? (
        <p className="m-0 text-sm text-[#666666]">
          {isError
            ? 'Could not look up a reference dose right now.'
            : weightSaved
              ? 'Weight saved. No reference dose on file for this medicine.'
              : 'No reference dose on file for this medicine.'}
        </p>
      ) : null}
    </div>
  )
}

export default ReferenceDosePanel
